import { useTranslation } from 'react-i18next';
import { findCategoryById, removeComponentFromQuery, type ParsedQuery } from '@barter/shared';

/**
 * Чіпси того, що розумний пошук зрозумів із вільного запиту: і «глупий»,
 * і «розумний» користувач бачить розбір («🔧 Двигун», «🚗 Nissan Leaf»,
 * «⬇️ задня») і може прибрати зайвий компонент одним кліком.
 */

const SIDE_ICONS: Record<string, string> = {
  front: '⬆️',
  rear: '⬇️',
  left: '⬅️',
  right: '➡️',
};

interface Chip {
  key: string;
  icon: string;
  label: string;
  tokenIdx: number[];
}

export function QueryChips({
  parsed,
  onRemove,
}: {
  parsed: ParsedQuery;
  onRemove: (nextQuery: string) => void;
}) {
  const { t } = useTranslation();

  const chips: Chip[] = [];

  if (parsed.make && parsed.model) {
    chips.push({
      key: 'who',
      icon: '🚗',
      label: `${parsed.make.value} ${parsed.model.name}`,
      tokenIdx: [...parsed.make.tokenIdx, ...parsed.model.tokenIdx],
    });
  } else if (parsed.make) {
    chips.push({ key: 'make', icon: '🚗', label: parsed.make.value, tokenIdx: parsed.make.tokenIdx });
  } else if (parsed.model) {
    chips.push({
      key: 'model',
      icon: '🚗',
      label: `${parsed.model.make} ${parsed.model.name}`,
      tokenIdx: parsed.model.tokenIdx,
    });
  }

  if (parsed.group) {
    const cat = findCategoryById(parsed.group.id);
    chips.push({
      key: 'group',
      icon: cat?.icon ?? '🔧',
      label: t(cat?.nameKey ?? parsed.group.id),
      tokenIdx: parsed.group.tokenIdx,
    });
  }

  parsed.sides.forEach((side, i) => {
    chips.push({
      key: `side-${i}`,
      icon: SIDE_ICONS[side.value] ?? '↔️',
      label: t(`search.side.${side.value}`),
      tokenIdx: side.tokenIdx,
    });
  });

  if (parsed.volume) {
    chips.push({
      key: 'volume',
      icon: '📏',
      label: `${parsed.volume.value} ${t('search.liter')}`,
      tokenIdx: parsed.volume.tokenIdx,
    });
  }

  if (parsed.condition) {
    chips.push({
      key: 'condition',
      icon: parsed.condition.value === 'new' ? '✨' : '♻️',
      label: parsed.condition.value === 'new' ? t('search.conditionNew') : t('search.conditionUsed'),
      tokenIdx: parsed.condition.tokenIdx,
    });
  }

  for (const kw of parsed.keywords) {
    chips.push({
      key: `kw-${kw.tokenIdx}`,
      icon: '🔎',
      label: parsed.tokens[kw.tokenIdx]?.raw ?? kw.stem,
      tokenIdx: [kw.tokenIdx],
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="search-chips">
      {chips.map((chip) => (
        <span key={chip.key} className="search-chip">
          <span className="search-chip__icon">{chip.icon}</span>
          <span>{chip.label}</span>
          <button
            type="button"
            className="search-chip__remove"
            aria-label={t('search.removeChip')}
            onClick={() => onRemove(removeComponentFromQuery(parsed, chip.tokenIdx))}
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );
}
